/* jshint esversion: 6 */

/**
 * Controller for managing the sidebar question list.
 * This class handles the initialization, interaction, and state management of the sidebar question list.
 */
class SidebarQuestionlistController {
    /**
     * Constructs the SidebarQuestionlistController.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $rootScope - AngularJS root scope.
     * @param {Object} $scope - AngularJS scope.
     * @param {Object} $location - AngularJS location service.
     * @param {Object} $signals - AngularJS signals service.
     * @param {Object} $routeParams - AngularJS route parameters.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} $translate - AngularJS translation service.
     */
    constructor($injector, $rootScope, $scope, $location, $signals, $routeParams, $timeout, $translate) {
        this.$injector = $injector;
        this.$rootScope = $rootScope;
        this.$scope = $scope;
        this.$location = $location;
        this.$signals = $signals;
        this.$routeParams = $routeParams;
        this.$timeout = $timeout;
        this.$translate = $translate;

        this.currentGroup = false;
        this.taskUUID = null;
        this._config = null;
        this._TaskInfoService = null;
        this._TaskSelectionService = null;
        this._TaskDataService = null;
        this._LogBridgeService = null;
        this._SheetLeaveService = null;
        this._taskContext = null;
        this.sheetKeyId = null;

        // Bind controller methods to $scope
        this.$scope.onGroupItemClick = this.onGroupItemClick.bind(this);
        this.$scope.onSheetItemClick = this.onSheetItemClick.bind(this);
        this.$scope.onQuestionItemClick = this.onQuestionItemClick.bind(this);
        this.$scope.onSingleQuestionSheetItemClick = this.onSingleQuestionSheetItemClick.bind(this);
        this.$scope.getQuestionsLength = this.getQuestionsLength.bind(this);
        this.$scope.checkLanguageAndChangeTitle = this.checkLanguageAndChangeTitle.bind(this);

        this.initialize();
    }

    /**
     * Retrieves the configuration object.
     * @returns {Object} The configuration object.
     */
    $config() {
        if (!this._config) {
            this._config = this.$injector.get('$config');
        }
        return this._config;
    }

    /**
     * Retrieves the TaskInfoService.
     * @returns {Object} The TaskInfoService instance.
     */
    TaskInfoService() {
        if (!this._TaskInfoService) {
            this._TaskInfoService = this.$injector.get('TaskInfoService');
        }
        return this._TaskInfoService;
    }

    /**
     * Retrieves the TaskSelectionService.
     * @returns {Object} The TaskSelectionService instance.
     */
    TaskSelectionService() {
        if (!this._TaskSelectionService) {
            this._TaskSelectionService = this.$injector.get('TaskSelectionService');
        }
        return this._TaskSelectionService;
    }

    /**
     * Retrieves the TaskDataService.
     * @returns {Object} The TaskDataService instance.
     */
    TaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Retrieves the LogBridgeService.
     * @returns {Object} The LogBridgeService instance.
     */
    LogBridgeService() {
        if (!this._LogBridgeService) {
            this._LogBridgeService = this.$injector.get('LogBridgeService');
        }
        return this._LogBridgeService;
    }

    /**
     * Retrieves the SheetLeaveService.
     * @returns {Object} The SheetLeaveService instance.
     */
    SheetLeaveService() {
        if (!this._SheetLeaveService) {
            this._SheetLeaveService = this.$injector.get('SheetLeaveService');
        }
        return this._SheetLeaveService;
    }

    /**
     * Initializes the controller.
     * This method sets up the initial state and fetches necessary data.
     */
    initialize() {
        const taskInfo = this.TaskInfoService().getTaskInfo();
        this.$scope.writingDirection = taskInfo.getInputLanguage().writingDirection;
        this.$scope.langISO = taskInfo.getISOInputLanguageCode();

        this.$scope._logIdentifier = 'SidebarQuestionlistController';
        const $logger = this.LogBridgeService();
        this._taskContext = this.TaskDataService().fetchContext();

        $logger.logEnter(this.$scope, 'init');

        if (angular.isUndefined(this.$scope.task) || !angular.isFunction(this.$scope.task.getUUID)) {
            this.TaskSelectionService().detectTaskUUIDFromUserInfo();
            this.taskUUID = this.TaskSelectionService().getTaskUUID();
            this.$scope.task = this._taskContext.read(this.taskUUID);
        }
        if (this.$scope.task && this.taskUUID !== this.$scope.task.getUUID()) {
            this.currentGroup = false;
            this.taskUUID = this.$scope.task.getUUID();
        }
        this.$scope.groups = this.$scope.task.getGroupedSheets();
        this.$scope.currentGroup = this.currentGroup ? this.currentGroup : this.$scope.groups[0];

        this.openSheets();

        $logger.logExit(this.$scope, 'init');
    }

    /**
     * Sets the sheet key ID.
     * @param {Object} sheet - The sheet object.
     */
    setSheetKeyId(sheet) {
        this.sheetKeyId = JSON.stringify(sheet.getUUID());
    }

    /**
     * Opens the sheets and sets the current group.
     */
    openSheets() {
        const sheetUUID = this.$routeParams.sheetUUID;
        this.sheetKeyId = JSON.stringify(sheetUUID);

        if (this.$routeParams.sheetUUID) {
            this.$timeout(() => {
                const sheet = this._taskContext.read(sheetUUID);
                if (sheet) {
                    this.onSheetItemClick(sheet);
                }
            }, 1);

            angular.forEach(this.$scope.groups, (group) => {
                angular.forEach(group, (sheet) => {
                    if (sheet.getUUID() === sheetUUID) {
                        this.$scope.currentGroup = group;
                    }
                });
            });
        }
    }

    /**
     * Handles the click event on a group item.
     * @param {Object} group - The group object.
     */
    onGroupItemClick(group) {
        this.currentGroup = group;
        this.openSheets();
        this.$scope.currentGroup = group;
    }

    /**
     * Performs a scroll animation to the target UUID.
     * @param {string} targetUUID - The target UUID.
     */
    scrollAnimation(targetUUID) {
        const targetSelector = 'question-headline[question="' + targetUUID + '"]';
        const halfViewport = $(window).height() * 0.01;
        let scrollTopTo = $(document).scrollTop();
        let targetY = $(document).scrollTop();
        if ($(targetSelector).length > 0) {
            targetY = $(targetSelector).position().top;

            if (targetY > halfViewport) {
                targetY = targetY - halfViewport;
            }
        }
        scrollTopTo = targetY;

        $('html, body').stop().animate({
            'scrollTop': scrollTopTo
        }, 900, 'swing');
    }

    /**
     * Handles the click event on a sheet item.
     * @param {Object} sheet - The sheet object.
     */
    onSheetItemClick(sheet) {
        const uuid = sheet.getUUID();
        const targetSelector = '#question-group-' + uuid;
        const sheetSelector = '.sheet-item-' + uuid;
        const arrowSelector = 'span.menu-toggle-arrow';
        const collapseTrigger = 'in';
        const arrowOpenClass = 'open';

        $(sheetSelector).find(arrowSelector).toggleClass(arrowOpenClass);
        $(targetSelector).toggleClass(collapseTrigger);
    }

    /**
     * Handles the click event on a question item.
     * @param {Object} questionPart - The question part object.
     */
    onQuestionItemClick(questionPart) {
        const sheet = this.$scope.task.findSheetWithQuestionPart(questionPart);
        let sheetUrl = '/sheet/' + sheet.getUUID() + '/' + questionPart.getUUID();

        if (window.isCMS) {
            sheetUrl = '/sheet/' + this.$scope.task.getUUID() + '/' + sheet.getUUID() + '/' + questionPart.getUUID();

            if (this.$rootScope.isPreview) {
                sheetUrl += '/preview';
            }
        }

        const result = this.SheetLeaveService().sheetLeaveSuccess(true);
        if (this.sheetKeyId && this.sheetKeyId !== JSON.stringify(sheet.getUUID())) {
            if (result === true) {
                this.$location.path(sheetUrl);
            } else {
                result.result.then(
                    () => {
                        // Success callback
                    },
                    () => {
                        this.$location.path(sheetUrl);
                    }
                );
            }
        } else {
            if (!this.sheetKeyId) {
                this.$location.path(sheetUrl);
            } else {
                this.scrollAnimation(questionPart.getUUID());
            }
        }
        this.setSheetKeyId(sheet);
    }

    /**
     * Handles the click event on a single question sheet item.
     * @param {Object} sheet - The sheet object.
     */
    onSingleQuestionSheetItemClick(sheet) {
        this.setSheetKeyId(sheet);

        let taskUUID = '';
        const questionPart = sheet.getQuestionParts()[0];

        if (window.isCMS && this.$routeParams.taskUUID) {
            taskUUID = this.$routeParams.taskUUID + '/';
        }

        let sheetUrl = '/sheet/' + taskUUID + sheet.getUUID();

        if (questionPart.isBookmarked()) {
            sheetUrl += '/' + questionPart.getUUID();
        }

        if (this.$rootScope.isPreview) {
            sheetUrl += '/preview';
        }

        const result = this.SheetLeaveService().sheetLeaveSuccess(true);
        if (result === true) {
            this.$location.path(sheetUrl);
        } else {
            result.result.then(
                () => {
                    // Success callback
                },
                () => {
                    this.$location.path(sheetUrl);
                }
            );
        }
    }

    /**
     * Gets the length of questions in a sheet.
     * @param {Object} sheet - The sheet object.
     * @returns {number|boolean} The number of questions or false if conditions are not met.
     */
    getQuestionsLength(sheet) {
        if (window.isCMS && !this.$config().isOnEditSheet()) {
            return false;
        }

        if (typeof sheet !== 'undefined') {
            return sheet.getQuestionParts().length;
        }
        return false;
    }

    /**
     * Checks the language and changes the title accordingly.
     * @param {Object} sheet - The sheet object.
     * @returns {string} The modified title.
     */
    checkLanguageAndChangeTitle(sheet) {
        if (this.$scope.langISO === 'sw_SW') {
            const totalMarks = sheet.getTotalMarks();
            const translatedMark = this.$translate.instant('CONTENT.MARK', { totalMarks: totalMarks });
            return ' (' + translatedMark + ' ' + totalMarks + ')';
        }
        return ' (' + sheet.getTotalMarks() + ' ' + this.$translate.instant('CONTENT.MARK', { totalMarks: sheet.getTotalMarks() }) + ') ';
    }
}

export default SidebarQuestionlistController;